import axios from "axios";
import { useContext, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { FaFilePdf } from "react-icons/fa";
import { AppContext } from "../../AppProvider";
import CBSGCharLoader from "../../Page/CBSGCharLoader";
import ImgCarousel from "../Works/ImageCurosol";

const ReportsPagination = () => {
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    phone: "6",
  });
  const [captions, setCaptions] = useState([]);
  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = reports.slice(indexOfFirstReport, indexOfLastReport);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const { uri } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [err1, res1] = await axios.get(`${uri}reports/`).then(r => [null, r]).catch(e => [e]);
      const [err2, res2] = await axios.get(`${uri}captions/`).then(r => [null, r]).catch(e => [e]);
      if (err1 || err2) return setError(err1 || err2), setLoading(false);
      setReports(res1.data);
      setCaptions(res2.data[0]);
      setLoading(false);
    })();
  }, [uri]);

  if (loading) {
    return (
      <div className="text-center">
        <CBSGCharLoader />
      </div>
    );
  }
  if (error) {
    return <p>Error fetching reports: {error.message}</p>;
  }

  const openModal = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ name: "", email: "", description: "" });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const Data = { ...formData, phone: "00", report: selectedReport.id };
    toast.promise(
      axios.post(`${uri}request-forms/`, Data),
      {
        pending: "Submitting request...",
        success: "Your request has been submitted for approval! 🎉",
        error: "Failed to submit request. ❌",
      }
    ).then(() => closeModal())
    .catch((error) => console.error("Error:", error));
  };

  return (
    <div className="p-4 sm:p-6 bg-sky-50 shadow-lg rounded-lg max-w-7xl mx-auto min-h-[80vh]">
      <h2 className="text-xl md:text-3xl font-bold mb-4 text-center">Our works</h2>
      <p className="text-sm sm:text-base w-full text-center mb-4">CBSG has completed over 380 consultancies in research, evaluation, and institutional development, producing reports, systems, policies, and practical insights. Many of these outputs are publicly shareable to support broader learning and sectoral progress. This page includes a curated list of available documents, accessible upon request</p>
      <div className="mb-4">
        <ImgCarousel images={captions.images}/>
      </div>
      <p className="font-semibold text-base sm:text-lg border-b mb-2">Report list:</p>
      <ul className="space-y-2">
        {currentReports.map((report, index) => (
          <li
            key={index}
            className="border-b py-2 gap-2 sm:gap-5 flex flex-col sm:flex-row justify-between items-center md:items-start sm:items-center hover:bg-slate-200 px-2"
          >
            <span className="text-2xl sm:text-3xl text-gray-600 ">
              <FaFilePdf />
            </span>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full">
              <div className="w-full sm:w-auto">
                <p className="font-semibold text-sm sm:text-base text-start">{report.title}</p>
                <div
                  className="text-xs sm:text-sm text-gray-600 text-start line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: report.description }}
                />
              </div>
              <button
                onClick={() => openModal(report)}
                className="bg-blue-500 text-white px-4 py-2 mt-2 sm:mt-0 rounded-lg hover:bg-blue-600 w-full sm:w-auto"
              >
                Download
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex justify-center mt-4 flex-wrap gap-2">
        {[...Array(Math.ceil(reports.length / reportsPerPage)).keys()].map(
          (number) => (
            <button
              key={number + 1}
              onClick={() => paginate(number + 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === number + 1
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {number + 1}
            </button>
          )
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4 z-30">
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-lg w-full max-w-[95%] sm:max-w-lg mx-auto">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-center">
              Request Access to Download
            </h2>
            <p className="mb-2 text-xs sm:text-sm md:text-base text-gray-600 text-center">
              Submit your details to request access for{" "}
              <strong>{selectedReport?.title}</strong>.
            </p>

            <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full border p-1.5 sm:p-2 rounded outline-none focus:ring focus:ring-blue-300 text-sm sm:text-base"
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border p-1.5 sm:p-2 rounded outline-none focus:ring focus:ring-blue-300 text-sm sm:text-base"
                required
              />
              <textarea
                name="description"
                placeholder="Purpose of reading this report"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full border p-1.5 sm:p-2 rounded outline-none focus:ring focus:ring-blue-300 min-h-[80px] sm:min-h-[100px] text-sm sm:text-base"
                required
              />
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="bg-gray-400 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded hover:bg-gray-500 w-full text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded hover:bg-blue-600 w-full text-sm sm:text-base"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Toaster position="top-center" />
    </div>
  );
};

export default ReportsPagination;
